<template>
  <BaseModal @close="$emit('close')" cardWidth="400px" noPadding noToolbarPadding noExit>
    <template #toolbar>
      <div class="flex w-full gap-3 pb-5 pt-1">
        <div class="relative" style="width: 44px; height: 44px">
          <img src="../../assets/icons/new-folder-icon.svg" />
        </div>
        <div class="flex-grow flex flex-col">
          <div class="font-medium text-text-loud">
            Move '{{ item.name }}'
          </div>
          <div class="text-sm text-text-normal">
            Change the folder for this {{ itemType }}
          </div>
        </div>
        <div class="ml-auto">
          <div id="close-wrapper" @click="$emit('close')">
            <TagRemoveIcon stroke="#5E6678" class="cursor-pointer"/>
          </div>
        </div>
      </div>
    </template>
    <template #default>
      <div class="flex items-center gap-4 p-6 border-t border-border-normal" style="height: 84px">
        <div class="relative flex-grow">
          <BaseSingleDropdown 
            use-parent-width
            enable-search
            search-placeholder="Search Folders"
            :options="folderOptions"
            :selected-obj="selectedFolder"
            @change="selectedFolder = $event"
          >
            <template #icon>
              <FolderIcon class="text-icon-normal" />
            </template>
          </BaseSingleDropdown>
        </div>
        <BaseButton primary iconOnly animate class="w-20"
          :disabled="!changesMade || loading" 
          :loading="loading" 
          :class="{'cursor-default': !changesMade}"
          @click="() => { if (changesMade && !loading) handleFormSubmit() }"
        >
          <BaseText type="label" size="sm" class="w-full text-center transition-colors"
          :class="changesMade ? 'text-white' : 'text-text-disabled'">
            Apply
          </BaseText>
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import LensAPI from '@/api/lens'
import { findFolderForBrand, getBrandFolderRef } from '@/utils/spyder/brandFolderUtils'

// Icons
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import FolderIcon from '../globals/Icons/SpyderV2Icons/FolderIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import NewCheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'

export default {
  name: 'ChangeFolderModal',
  mixins: [clickaway],
  components: {
    TagRemoveIcon,
    FolderIcon,
    ChevronIcon,
    NewCheckmarkIcon
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    itemType: {
      type: String,
      required: true
    },
    // Only pass if moving a lens report
    lensId: {
      type: String
    },
    initLensFolderId: {
      type: String
    }
  },
  data () {
    return {
      currentFolder: {},
      selectedFolder: {},
      expanded: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters('BoardsModule', ['getFolders']),
    ...mapGetters('LensModule', ['getLensFolders']),
    folderOptions () {
      if (this.lensId) return this.lensFolders
      return this.mainFolders
    },
    changesMade () {
      return this.currentFolder.id !== this.selectedFolder.id
    },
    mainFolders () {
      if (this.lensId) return []
      const folders = this.getFolders.filter(folder => folder.id !== this.selectedFolder.id)
      if (this.selectedFolder.id) {
        folders.push({ id: null, name: 'Default Folder' })
      }
      return folders.sort((a, b) => a.name.localeCompare(b.name)) // sort alphabetically
    },
    lensFolders () {
      if (!this.lensId) return []
      const folders = [...(this.getLensFolders?.[this.lensId] || [])]
      if (folders.length) folders[0] = { ...folders[0], name: 'Custom Reports' }
      return folders
    }
  },
  mounted () {
    if (this.lensId && this.initLensFolderId) {
      this.currentFolder = this.lensFolders.find(folder => folder.id === this.initLensFolderId)
    } else {
      this.currentFolder = (this.itemType === 'brand'
        ? findFolderForBrand(this.getFolders, this.item)
        : this.itemType === 'board'
          ? this.getFolders.find(folder => folder?.boardIds?.includes(this.item.id))
          : this.getFolders.find(folder => folder?.briefIds?.includes(this.item.id))
      ) || { id: null, name: 'Default Folder'}
    }
    this.selectedFolder = this.currentFolder
  },
  methods: {
    ...mapActions('BoardsModule', ['fetchFolders']),
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    handleFormSubmit () {
      if (this.lensId) {
        this.applyLensFolderChange()
      } else {
        this.applyFolderChange()
      }
    },
    async applyFolderChange () {
      this.loading = true
      try {
        if (this.itemType === 'brand') {
          // The Spyder API files the brand (subscribed or pending) and takes it out of its old folder
          await ForeplayAPI.Spyder.moveBrandToFolder(getBrandFolderRef(this.item), this.selectedFolder.id)
        } else {
          // Remove board/brief from old folder (if old is not default)
          if (this.currentFolder.id) {
            const toRemovePayload = this.itemType === 'board'
              ? { boardIds: this.currentFolder.boardIds.filter(id => id !== this.item.id) }
              : { briefIds: this.currentFolder.briefIds.filter(id => id !== this.item.id) }
            await FirebaseAPI.Folders.update(this.currentFolder.id, toRemovePayload)
          }
          // Add board/brief to new folder (if new is not default)
          if (this.selectedFolder.id) {
            const toAddPayload = this.itemType === 'board'
              ? { boardIds: [...this.selectedFolder.boardIds, this.item.id] }
              : { briefIds: [...(this.selectedFolder?.briefIds || []), this.item.id] }
            await FirebaseAPI.Folders.update(this.selectedFolder.id, toAddPayload)
          }
        }
        this.$showAlert({
          message: `${this.item.name || 'Brand'} moved to ${this.selectedFolder.name}`,
          type: 'success'
        })
      } catch (error) {
        console.error('Failed to change folder:', error)
        this.$showAlert({
          message: error.response?.data?.message || 'Failed to change folder, see console for details.',
          type: 'error'
        })
      }
      await this.fetchFolders() // Refresh folders in store
      this.loading = false
      this.$emit('onFolderMove')
      this.$emit('close')
    },
    async applyLensFolderChange () {
      this.loading = true
      try {
        if (!this.item?.id || !this.selectedFolder?.id) {
          throw new Error('No item or selected folder')
        }
        await LensAPI.LensReport.update(this.item.id, { folder_id: this.selectedFolder.id })
        await this.fetchSidebarLenses()
        this.$showAlert({
          message: `${this.item.name} moved to ${this.selectedFolder.name}`,
          type: 'success'
        })
      } catch (error) {
        console.error('Failed to change folder:', error)
        this.$showAlert({
          message: 'Failed to change folder, see console for details.',
          type: 'error'
        })
      } finally {
        this.loading = false
        this.$emit('close')
      }
    }
  }
}
</script>

<style scoped>
.folder-dropdown-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  overflow: hidden;
}
.folder-dropdown {
  transition: height 100ms ease-in-out;
}
.folder-dropdown.expanded {
  border-left: 1px solid; 
  border-right: 1px solid; 
  border-bottom: 1px solid; 
  border-color: #ECEFF3;
}
.dropdown-fade-overlay {
  position: absolute;
  pointer-events: none;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  z-index: 40;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffffb9 100%);
}
</style>